import { Component, Fragment } from 'react';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';

import { Button } from '~/eds/src/components/Button/Button';
import { DateInput } from '~/eds/src/components/DateInput/DateInput';
import { Icon } from '~/eds/src/components/Icon/Icon';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { parseDate } from '~/eds/src/utils/parseDate';
import { DataFieldType } from '~/enums/DataFieldType';
import { FlagType } from '~/flags/flags';
import { withFlags } from '~/flags/withFlags';
import { FormattedMessage, injectIntl } from 'react-intl';
import { parseUserDate } from '~/i18n/utils';
import { withFormatDate } from '~/i18n/hocs/withFormatDate';
import { getClientContractTypes } from '~/redux/api/methods/client/getClientContractTypes';
import { testIsSuperAdmin } from '~/utils/user';

import {
  black2,
  black5,
  evisortBlue,
} from '../../../assets/shared-styles/general';
import EcCheckbox from '../../Shared/EcCheckbox/EcCheckbox';
import EcDatepicker from '../../Shared/EcDatepicker/EcDatepicker';
import EcInput from '../../Shared/EcInput/EcInput';
import EcModalCard from '../../Shared/EcModalCard/EcModalCard';
import EcMultipleSelectValue from '../../Shared/EcMultipleSelectValue/EcMultipleSelectValue';
import EcMultiSelect from '../../Shared/EcMultiSelect/EcMultiSelect';
import EcRadioButton from '../../Shared/EcRadioButton/EcRadioButton';
import EcRadioContainer from '../../Shared/EcRadioContainer/EcRadioContainer';
import EcSelect from '../../Shared/EcSelect/EcSelect';
import ChevronDownIcon from '../../Shared/Icons/ChevronDownIcon';
import ChevronRightIcon from '../../Shared/Icons/ChevronRightIcon';
import SubArrowIcon from '../../Shared/Icons/SubArrowIcon';
import styles from './CustomFieldModal.styles';

const field_types_available = [
  'Attachment',
  'Date',
  'Dropdown',
  'Number',
  'Text',
  'Text Area',
  'Reference',
];

const fieldTypesDisplay = {
  ARRAY_SINGLE: 'Dropdown',
  ARRAY_MULTIPLE: 'Dropdown',
  ATTACHMENT: 'Attachment',
  BOOLEAN: 'True/False',
  DATE: 'Date',
  NUMBER: 'Number',
  STRING: 'Text',
  TEXT_AREA: 'Text Area',
  REFERENCE: 'Reference',
};

class CustomFieldModal extends Component {
  constructor(props) {
    super(props);

    const { intl } = props;
    const defaultFieldTypeValue = {
      label: intl.formatMessage({
        id: 'CustomFieldModal.textFieldType2',
        defaultMessage: 'Text',
        description: 'Default field type label',
      }),
      value: 'Text',
    };

    this.state = {
      selectedItem: this.props.selectedItem,
      editData: {
        name: this.props.selectedItem ? this.props.selectedItem.label : '',
        field_type: this.props.selectedItem
          ? {
              label: fieldTypesDisplay[this.props.selectedItem.field_type],
              value: fieldTypesDisplay[this.props.selectedItem.field_type],
            }
          : defaultFieldTypeValue,
        rawOptions: this.props.selectedItem
          ? this.convertTypeToLabel(this.props.selectedItem.options)
          : [],
        options: this.props.selectedItem
          ? this.convertTypeToLabel(this.props.selectedItem.options)
          : [],
        doc_restriction: this.props.selectedItem
          ? this.props.selectedItem.doc_restriction
          : 'EXCLUDE',
        doc_types: this.props.selectedItem
          ? this.convertTypeToLabel(this.props.selectedItem.doc_types)
          : [],
        defaultValue: this.props.selectedItem
          ? this.props.selectedItem.default
          : null,
        allow_multiple: this.props.selectedItem
          ? this.props.selectedItem.allow_multiple
          : false,
        allow_new_options: this.props.selectedItem
          ? this.props.selectedItem.allow_new_options
          : false,
        sort_options: this.props.selectedItem
          ? this.props.selectedItem.sort_options
          : false,
        help_text: this.props.selectedItem
          ? this.props.selectedItem.help_text
          : '',
        show_on_upload_form: this.props.selectedItem
          ? this.props.selectedItem.show_on_upload_form
          : false,
        is_beta: this.props.selectedItem
          ? this.props.selectedItem.is_beta
          : false,
        is_smart: this.props.selectedItem
          ? this.props.selectedItem.is_smart
          : false,
        is_visible: this.props.selectedItem
          ? this.props.selectedItem.is_visible
          : true,
      },
    };

    this.handleActionClick = this.handleActionClick.bind(this);
    this.handleFlagForUpload = this.handleFlagForUpload.bind(this);
    this.handleAllowNewOptionsCheckbox =
      this.handleAllowNewOptionsCheckbox.bind(this);
  }

  componentDidMount() {
    const { clientId, onGetFormDataRef } = this.props;

    getClientContractTypes({ clientId })
      .then((suggestions) => {
        this.setState({
          docTypes: this.convertTypeToLabel(suggestions),
          errorLoadingDocTypes: false,
        });
      })
      .catch(() => this.setState({ errorLoadingDocTypes: true }));

    this.updateValidationState(this.state.editData);

    if (onGetFormDataRef) {
      onGetFormDataRef({
        getFormData: () => this.getFormData(),
        handleActionClick: () => this.handleActionClick(),
      });
    }
  }

  updateValidationState(editData) {
    const { onValidationChange } = this.props;
    if (!onValidationChange) return;

    const {
      name,
      defaultValue,
      field_type,
      allow_new_options,
      options,
      doc_restriction,
      doc_types,
    } = editData;

    const multiValuesConditionNotOk =
      !!field_type &&
      field_type.value === 'Dropdown' &&
      !allow_new_options &&
      !options.length;

    const multiValuesHasNoMatchingDefault =
      !!field_type &&
      field_type.value === 'Dropdown' &&
      defaultValue &&
      defaultValue.length &&
      options &&
      options.filter((option) => option.value === defaultValue).length === 0;

    const disabledSaveBtn =
      !name ||
      multiValuesConditionNotOk ||
      multiValuesHasNoMatchingDefault ||
      (doc_restriction === 'INCLUDE' && !doc_types.length);

    onValidationChange(disabledSaveBtn);
  }

  convertTypeToLabel(types) {
    return types && types.length
      ? types.map((type) => {
          return {
            label: type,
            value: type,
            key: type,
          };
        })
      : [];
  }

  handleActionClick() {
    const { handleDataFieldChange, hideModal, hideFooter } = this.props;
    const { editData } = this.state;

    handleDataFieldChange(editData);
    if (!hideFooter && hideModal) {
      hideModal();
    }
  }

  getFormData() {
    return this.state.editData;
  }

  handleFormChange(name, value) {
    const { editData } = this.state;
    const newEditData = { ...editData, [name]: value };
    this.setState({ editData: newEditData }, () => {
      this.updateValidationState(newEditData);
    });
  }

  renderMultiSelect(item, name) {
    const multiSelectMenu = this.renderItem(item, name);
    return <div>{multiSelectMenu}</div>;
  }

  renderItem(item, name) {
    const { selectedItem, intl } = this.props;
    const {
      editData: { rawOptions },
    } = this.state;

    const presentValue = this.state.editData[name] || [];
    const options = selectedItem && name === 'options' ? rawOptions : item;
    const notCreatable = name === 'doc_types';

    const placeholderText = intl.formatMessage({
      id: 'CustomFieldModal.enterPlaceholder',
      defaultMessage: 'Enter value...',
      description: 'Multi-select placeholder text',
    });

    const noOptionsText = intl.formatMessage({
      id: 'CustomFieldModal.noOptionsFound',
      defaultMessage: 'No value found',
      description: 'No options message for multi-select',
    });

    return (
      <div>
        <EcMultiSelect
          key={`select-menu-${name}`}
          value={presentValue}
          options={options}
          onChange={(newItems) => {
            const newState = {};
            newState[name] = newItems;
            const newEditData = { ...this.state.editData, ...newState };
            this.setState({ editData: newEditData }, () => {
              this.updateValidationState(newEditData);
            });
          }}
          placeholder={placeholderText}
          noOptionsMessage={noOptionsText}
          isCreatable={!notCreatable}
        />
        {this.renderItemValues(name)}
      </div>
    );
  }

  renderItemValues(name) {
    const { classes } = this.props;
    const presentValues = this.state.editData[name];

    return (
      <div className={classes.multipleValuesContainer}>
        <SubArrowIcon color={black5} />
        <div className={classes.multipleValues}>
          {presentValues.length ? (
            presentValues.map((value) => (
              <EcMultipleSelectValue
                label={value.label}
                key={value.value}
                closeHandler={() => {
                  const newState = {};
                  const newMultiState = [...this.state.editData[name]];
                  newState[name] = newMultiState.filter(
                    (multiValue) => multiValue.value !== value.value,
                  );
                  const newEditData = { ...this.state.editData, ...newState };
                  this.setState({ editData: newEditData }, () => {
                    this.updateValidationState(newEditData);
                  });
                }}
              />
            ))
          ) : (
            <span className={classes.multipleValuesEmpty}>
              <FormattedMessage
                id="CustomFieldModal.noValuesSelected"
                defaultMessage="No values selected..."
                description="Empty state message for multi-select values"
              />
            </span>
          )}
        </div>
      </div>
    );
  }

  renderInputFieldForType(field_type) {
    let returnValue;

    if (field_type) {
      switch (field_type.label) {
        case 'Date':
          returnValue = this.renderDateItem(field_type);
          break;
        case 'Number':
          returnValue = this.renderNumberItem(field_type);
          break;
        case 'Text Area':
          returnValue = this.renderTextAreaItem(field_type);
          break;
        default:
          returnValue = this.renderTextItem(field_type);
          break;
      }
      return returnValue;
    }
  }

  renderDateItem() {
    const {
      editData: { defaultValue },
    } = this.state;
    const { flags, intl, formatDate } = this.props;
    const enableLocalization = flags[FlagType.LanguageSelect];

    return enableLocalization ? (
      <Layout>
        <DateInput
          id="CustomFieldModal/DefaultValue"
          value={defaultValue ? parseUserDate(defaultValue) : null}
          onChange={(date) => {
            const isoDate = date ? formatDate(date, 'iso') : null;
            const newEditData = {
              ...this.state.editData,
              defaultValue: isoDate,
            };
            this.setState({ editData: newEditData }, () => {
              this.updateValidationState(newEditData);
            });
          }}
        />
        <Button
          level="tertiary"
          text={intl.formatMessage({
            id: 'CustomFieldModal.clearDate',
            defaultMessage: 'Clear',
            description: 'Clear date button text',
          })}
          onClick={() => {
            this.setState({
              editData: { ...this.state.editData, defaultValue: null },
            });
          }}
        />
      </Layout>
    ) : (
      <EcDatepicker
        date={defaultValue ? parseDate(defaultValue) : null}
        onDateChange={(date) => {
          const newDate = date ? formatDate(date) : null;
          const newEditData = { ...this.state.editData, defaultValue: newDate };
          this.setState({ editData: newEditData }, () => {
            this.updateValidationState(newEditData);
          });
        }}
        id="CustomFieldModal/DefaultValue"
        isClearable={true}
        placeholder={intl.formatMessage({
          id: 'CustomFieldModal.selectDatePlaceholder',
          defaultMessage: 'Select date',
          description: 'Date picker placeholder text',
        })}
      />
    );
  }

  renderNumberItem() {
    const {
      editData: { defaultValue },
    } = this.state;
    const { intl } = this.props;

    return (
      <EcInput
        id="CustomFieldModal/DefaultValue"
        placeholder={intl.formatMessage({
          id: 'CustomFieldModal.defaultValuePlaceholder2',
          defaultMessage:
            'Enter the default value for this field (if applicable)',
          description: 'Default value input placeholder',
        })}
        type="number"
        input={{
          value: defaultValue || '',

          onChange: (event) => {
            this.handleFormChange('defaultValue', event.target.value);
          },
        }}
      />
    );
  }

  renderTextAreaItem() {
    const { classes } = this.props;
    return (
      <textarea
        id="CustomFieldModal/DefaultValue"
        rows="5"
        cols="66"
        className={classes.textArea}
        onChange={(e) => this.handleFormChange('defaultValue', e.target.value)}
      />
    );
  }

  renderTextItem(field_type) {
    const {
      editData: { options, defaultValue },
      selectedItem,
    } = this.state;
    const { intl } = this.props;

    const disableDefaultValue =
      (field_type && field_type.label === 'Dropdown' && !options.length) ||
      field_type.label === 'Attachment' ||
      (selectedItem &&
        !options.length &&
        [DataFieldType.ARRAY_MULTIPLE, DataFieldType.ARRAY_SINGLE].includes(
          selectedItem.field_type,
        )) ||
      (selectedItem && field_type === DataFieldType.ATTACHMENT);

    return (
      <EcInput
        id="CustomFieldModal/DefaultValue"
        isDisabled={disableDefaultValue}
        placeholder={intl.formatMessage({
          id: 'CustomFieldModal.defaultValuePlaceholder',
          defaultMessage:
            'Enter the default value for this field (if applicable)',
          description: 'Default value input placeholder',
        })}
        input={{
          value: defaultValue || '',
          onChange: (event) => {
            this.handleFormChange('defaultValue', event.target.value);
          },
        }}
      />
    );
  }

  renderFieldType() {
    const { classes, intl } = this.props;
    const { selectedItem } = this.state;
    const field_types_menu = field_types_available
      .filter((item) => selectedItem || item !== 'Reference')
      .map((item) => ({
        label: item,
        value: item,
      }));

    const defaultFieldTypeValue = {
      label: intl.formatMessage({
        id: 'CustomFieldModal.textFieldType',
        defaultMessage: 'Text',
        description: 'Default field type label',
      }),
      value: 'Text',
    };

    if (
      selectedItem &&
      (!!selectedItem.is_smart || selectedItem.static_key_name)
    ) {
      return null;
    }
    return (
      <Fragment>
        <div className={classes.divider} />
        <label
          className={classes.labelText}
          htmlFor="CustomFieldModal/FieldType"
        >
          <FormattedMessage
            id="CustomFieldModal.fieldTypeLabel"
            defaultMessage="FIELD TYPE"
            description="Field type section label"
          />
        </label>
        <EcSelect
          id="CustomFieldModal/FieldType"
          width="100%"
          {...(selectedItem && {
            value: {
              label: fieldTypesDisplay[selectedItem.field_type],
              value: fieldTypesDisplay[selectedItem.field_type],
            },
          })}
          defaultValue={defaultFieldTypeValue}
          options={field_types_menu}
          placeholder={intl.formatMessage({
            id: 'CustomFieldModal.selectFieldTypePlaceholder',
            defaultMessage: 'Select Field Type',
            description: 'Field type select placeholder',
          })}
          onChange={(value) => this.handleFormChange('field_type', value)}
          isDisabled={!!selectedItem}
        />
      </Fragment>
    );
  }

  renderDefaultValueHelpText() {
    const { classes, intl } = this.props;
    const {
      advancedOptionsExpanded,
      editData: { help_text, field_type },
      selectedItem,
    } = this.state;

    if (!advancedOptionsExpanded) {
      return null;
    }

    return (
      <div className={classes.advancedOptionsWrapper}>
        <label
          className={classes.labelText}
          htmlFor="CustomFieldModal/DefaultValue"
        >
          <FormattedMessage
            id="CustomFieldModal.defaultValueLabel"
            defaultMessage="DEFAULT VALUE"
            description="Default value section label"
          />
        </label>
        {this.renderInputFieldForType(field_type)}
        {(field_type && field_type.label === 'Dropdown') ||
        (selectedItem &&
          [DataFieldType.ARRAY_SINGLE, DataFieldType.ARRAY_MULTIPLE].includes(
            selectedItem.field_type,
          ))
          ? this.renderMultiSelectSelection()
          : null}

        <label
          className={classes.labelText}
          htmlFor="CustomFieldModal/HelpText"
        >
          <FormattedMessage
            id="CustomFieldModal.helpTextLabel"
            defaultMessage="HELP TEXT"
            description="Help text section label"
          />
        </label>
        <EcInput
          id="CustomFieldModal/HelpText"
          placeholder={intl.formatMessage({
            id: 'CustomFieldModal.helpTextPlaceholder',
            defaultMessage:
              'If necessary, explain in more detail the data for this field...',
            description: 'Help text input placeholder',
          })}
          input={{
            value: help_text || '',
            onChange: (event) => {
              this.handleFormChange('help_text', event.target.value);
            },
          }}
        />
      </div>
    );
  }

  handleOptionSelection(selectedDocTypeOption) {
    const newEditData = {
      ...this.state.editData,
      doc_types: [],
      doc_restriction: selectedDocTypeOption,
    };
    this.setState({ editData: newEditData }, () => {
      this.updateValidationState(newEditData);
    });
  }

  handleAllowNewOptionsCheckbox() {
    const { editData } = this.state;

    const checked = !editData.allow_new_options;
    const newEditData = { ...this.state.editData, allow_new_options: checked };
    this.setState({ editData: newEditData }, () => {
      this.updateValidationState(newEditData);
    });
  }

  renderDocumentTypes() {
    const { classes } = this.props;
    const { selectedItem, errorLoadingDocTypes } = this.state;

    if (selectedItem && !!selectedItem.is_smart) {
      return null;
    } else if (errorLoadingDocTypes) {
      return (
        <div>
          <FormattedMessage
            id="CustomFieldModal.errorLoadingDocTypes"
            defaultMessage="An error occurred while loading document types"
            description="Error message for document types loading failure"
          />
        </div>
      );
    }

    return (
      <div>
        <div className={classes.divider} />
        <div className={classes.labelText}>
          <FormattedMessage
            id="CustomFieldModal.documentTypesLabel"
            defaultMessage="DOCUMENT TYPES"
            description="Document types section label"
          />
        </div>
        <p className={classes.descriptionText}>
          <FormattedMessage
            id="CustomFieldModal.documentTypesDescription"
            defaultMessage="You can choose whether this data field will be shown on any documents or documents of a specific type."
            description="Document types section description"
          />
        </p>
        {this.renderDocumentTypesOptions()}
      </div>
    );
  }

  renderDocumentTypesOptions() {
    const { classes } = this.props;
    const {
      editData: { doc_restriction },
    } = this.state;

    return (
      <EcRadioContainer
        groupName="docTypesOptions"
        onChange={({ selectedValue }) =>
          this.handleOptionSelection(selectedValue)
        }
        defaultValue={doc_restriction}
      >
        {(groupName, selectedValue, onOptionChange) => (
          <div className={classes.radioContainerContent}>
            <EcRadioButton
              id="allDocTypesWithExceptions"
              groupName="docTypesOptions"
              value="EXCLUDE"
              label={
                <span>
                  <FormattedMessage
                    id="CustomFieldModal.allDocumentTypes"
                    defaultMessage="All document types"
                    description="All document types radio option"
                  />
                </span>
              }
              onChange={onOptionChange}
              selectedValue={doc_restriction}
              fontSize="13px"
              labelColor={black2}
            />

            {doc_restriction === 'EXCLUDE'
              ? this.renderDocTypesExceptions()
              : null}

            <EcRadioButton
              id="specificDocTypes"
              groupName="docTypesOptions"
              value="INCLUDE"
              label={
                <span>
                  <FormattedMessage
                    id="CustomFieldModal.specificDocumentTypes"
                    defaultMessage="Only <strong>specific</strong> document types"
                    description="Specific document types radio option"
                    values={{ strong: (chunks) => <strong>{chunks}</strong> }}
                  />
                </span>
              }
              onChange={onOptionChange}
              selectedValue={doc_restriction}
              fontSize="13px"
              labelColor={black2}
            />

            {doc_restriction === 'INCLUDE'
              ? this.renderDocTypesSpecific()
              : null}
          </div>
        )}
      </EcRadioContainer>
    );
  }

  renderMultiSelectSelection() {
    const { classes, intl } = this.props;
    const {
      editData: { allow_multiple, allow_new_options, sort_options },
    } = this.state;

    return (
      <div>
        <Fragment>
          <div className={classes.labelText}>
            <FormattedMessage
              id="CustomFieldModal.selectionLabel"
              defaultMessage="SELECTION"
              description="Selection section label"
            />
          </div>
          <EcRadioContainer
            groupName="allowMultiple"
            onChange={({ selectedValue }) =>
              this.handleFormChange('allow_multiple', selectedValue)
            }
            defaultValue={allow_multiple}
          >
            {(groupName, selectedValue, onOptionChange) => (
              <div className={classes.radioContainerContent}>
                <EcRadioButton
                  id="allowOneOption"
                  groupName="allowMultiple"
                  value={false}
                  label={intl.formatMessage({
                    id: 'CustomFieldModal.allowOneOption',
                    defaultMessage: 'Allow selection of one option',
                    description: 'Single selection radio option',
                  })}
                  onChange={onOptionChange}
                  selectedValue={allow_multiple}
                  fontSize="13px"
                  labelColor={black2}
                />

                <EcRadioButton
                  id="allowMultiOptions"
                  groupName="allowMultiple"
                  value={true}
                  label={intl.formatMessage({
                    id: 'CustomFieldModal.allowMultipleOptions',
                    defaultMessage: 'Allow selection of multiple options',
                    description: 'Multiple selection radio option',
                  })}
                  onChange={onOptionChange}
                  selectedValue={allow_multiple}
                  fontSize="13px"
                  labelColor={black2}
                />
              </div>
            )}
          </EcRadioContainer>
        </Fragment>

        <div className={classes.labelText}>
          <FormattedMessage
            id="CustomFieldModal.userProvidedOptionsLabel"
            defaultMessage="USER-PROVIDED OPTIONS"
            description="User-provided options section label"
          />
        </div>
        <span className={classes.checkboxContainer}>
          <EcCheckbox
            checked={allow_new_options}
            id="CustomFieldModal/AllowUserToAddOptionsCheckbox"
            onClick={this.handleAllowNewOptionsCheckbox}
          />
          <label
            className={classes.checkboxLabel}
            htmlFor="CustomFieldModal/AllowUserToAddOptionsCheckbox"
          >
            <FormattedMessage
              id="CustomFieldModal.allowUserAddOptions"
              defaultMessage="Allow user to add their own options"
              description="Allow user to add options checkbox label"
            />
          </label>
        </span>

        <div className={classes.labelText}>
          <FormattedMessage
            id="CustomFieldModal.optionsSortingLabel"
            defaultMessage="OPTIONS SORTING"
            description="Options sorting section label"
          />
        </div>
        <EcRadioContainer
          groupName="allowSort"
          onChange={({ selectedValue }) =>
            this.handleFormChange('sort_options', selectedValue)
          }
          defaultValue={sort_options}
        >
          {(groupName, selectedValue, onOptionChange) => (
            <div className={classes.radioContainerContent}>
              <EcRadioButton
                id="sortOptionsFalse"
                groupName="allowSort"
                value={false}
                label={intl.formatMessage({
                  id: 'CustomFieldModal.doNotSortOptions',
                  defaultMessage: 'Do not sort options',
                  description: 'Do not sort options radio option',
                })}
                onChange={onOptionChange}
                selectedValue={sort_options}
                fontSize="13px"
                labelColor={black2}
              />

              <EcRadioButton
                id="sortOptionsTrue"
                groupName="allowSort"
                value={true}
                label={intl.formatMessage({
                  id: 'CustomFieldModal.sortOptionsAlphabetically',
                  defaultMessage: 'Sort options alphabetically',
                  description: 'Sort options alphabetically radio option',
                })}
                onChange={onOptionChange}
                selectedValue={sort_options}
                fontSize="13px"
                labelColor={black2}
              />
            </div>
          )}
        </EcRadioContainer>
      </div>
    );
  }

  renderAvailableOption() {
    const { classes, selectedItem } = this.props;
    const {
      editData: { field_type, options },
    } = this.state;

    if (
      (field_type && field_type.label === 'Dropdown') ||
      (selectedItem &&
        !selectedItem.is_smart &&
        [DataFieldType.ARRAY_MULTIPLE, DataFieldType.ARRAY_SINGLE].includes(
          field_type,
        ))
    ) {
      return (
        <Fragment>
          <div className={classes.labelText}>
            <FormattedMessage
              id="CustomFieldModal.availableOptionsLabel"
              defaultMessage="AVAILABLE OPTIONS"
              description="Available options section label"
            />
          </div>
          {this.renderItem(options, 'options')}
        </Fragment>
      );
    }
    return null;
  }

  renderSuperAdminOptions() {
    const { classes, currentUser, intl } = this.props;
    const {
      advancedOptionsExpanded,
      editData: { is_beta, is_smart, is_visible },
    } = this.state;

    const isSuperAdmin = testIsSuperAdmin(currentUser);

    if (!isSuperAdmin || !advancedOptionsExpanded) {
      return null;
    }

    const handleIsBeta = () => this.handleFormChange('is_beta', !is_beta);
    const handleIsSmart = () => this.handleFormChange('is_smart', !is_smart);
    const handleIsVisible = () =>
      this.handleFormChange('is_visible', !is_visible);

    return (
      <div className={classes.flagContainer}>
        <span className={classes.checkboxContainer}>
          <EcCheckbox
            checked={is_visible}
            id="CustomFieldModal/DisplayFieldInSectionCheckbox"
            onClick={handleIsVisible}
          />
          <label
            className={classes.checkboxLabel}
            htmlFor="CustomFieldModal/DisplayFieldInSectionCheckbox"
          >
            <FormattedMessage
              id="CustomFieldModal.displayFieldInSection"
              defaultMessage="Display this field in a client section"
              description="Display field in section checkbox label"
            />
          </label>
        </span>
        <span className={classes.checkboxContainer}>
          <EcCheckbox
            checked={is_smart}
            id="CustomFieldModal/RelatedToAlgorithmCheckbox"
            onClick={handleIsSmart}
          />
          <label
            className={classes.checkboxLabel}
            htmlFor="CustomFieldModal/RelatedToAlgorithmCheckbox"
          >
            <FormattedMessage
              id="CustomFieldModal.relatedToAlgorithm"
              defaultMessage="Is this related to an algorithm?"
              description="Algorithm related checkbox label"
            />
          </label>
          <div
            className={classes.tooltip}
            data-tip
            data-for="isSmartFieldTooltip"
          >
            <Icon
              size="s"
              color="status.active"
              icon="help"
              placement="right"
              tooltip={intl.formatMessage({
                id: 'CustomFieldModal.smartFieldTooltip',
                defaultMessage:
                  'Updating this will mark this field as Standard AI for ALL clients.',
                description: 'Standard AI (formerly smart) field tooltip text',
              })}
              id="isSmartFieldTooltip"
            />
          </div>
        </span>
        {is_smart && (
          <Fragment>
            <span className={classes.checkboxContainer}>
              <EcCheckbox
                checked={is_beta}
                id="CustomFieldModal/RelatedToAlgorithmBetaTestingCheckbox"
                onClick={handleIsBeta}
              />
              <label
                className={classes.checkboxLabel}
                htmlFor="CustomFieldModal/RelatedToAlgorithmBetaTestingCheckbox"
              >
                <FormattedMessage
                  id="CustomFieldModal.relatedToAlgorithmBetaTesting"
                  defaultMessage="Is this related to an algorithm that we're beta testing?"
                  description="Beta testing algorithm checkbox label"
                />
              </label>
              <div
                className={classes.tooltip}
                data-tip
                data-for="isBetaFieldTooltip"
              >
                <Icon
                  size="s"
                  icon="help"
                  color="status.active"
                  placement="right"
                  tooltip={intl.formatMessage({
                    id: 'CustomFieldModal.betaFieldTooltip',
                    defaultMessage:
                      'Updating this will mark this field as BETA for ALL clients.',
                    description: 'Beta field tooltip text',
                  })}
                  id="isBetaFieldTooltip"
                />
              </div>
            </span>
          </Fragment>
        )}
      </div>
    );
  }

  renderAdvancedOptions() {
    const { classes, currentUser } = this.props;
    const { advancedOptionsExpanded, selectedItem } = this.state;
    const isSuperAdmin = testIsSuperAdmin(currentUser);

    if (
      !isSuperAdmin &&
      selectedItem &&
      (!!selectedItem.is_smart || selectedItem.static_key_name)
    ) {
      return null;
    }

    return (
      <div>
        <button
          className={classes.blueButton}
          onClick={() =>
            this.setState({ advancedOptionsExpanded: !advancedOptionsExpanded })
          }
        >
          {advancedOptionsExpanded ? (
            <ChevronDownIcon color={evisortBlue} size="16" />
          ) : (
            <ChevronRightIcon color={evisortBlue} size="16" />
          )}
          <span>
            <FormattedMessage
              id="CustomFieldModal.advancedOptions"
              defaultMessage="Advanced Options"
              description="Advanced options button text"
            />
          </span>
        </button>
        {this.renderDefaultValueHelpText()}

        {this.renderUploadFlag()}
        {this.renderSuperAdminOptions()}
      </div>
    );
  }

  handleFlagForUpload() {
    const { editData } = this.state;

    const checked = !editData.show_on_upload_form;
    const newEditData = {
      ...this.state.editData,
      show_on_upload_form: checked,
    };
    this.setState({ editData: newEditData }, () => {
      this.updateValidationState(newEditData);
    });
  }

  renderUploadFlag() {
    const { classes, intl } = this.props;
    const {
      advancedOptionsExpanded,
      editData: { show_on_upload_form, field_type },
    } = this.state;
    // TODO: Remove field_type check when ATTACHMENT on upload fields are supported
    if (
      !advancedOptionsExpanded ||
      field_type === DataFieldType.ATTACHMENT ||
      field_type?.label === 'Attachment'
    ) {
      return null;
    }
    return (
      <div className={classes.flagContainer}>
        <span className={classes.checkboxContainer}>
          <EcCheckbox
            checked={show_on_upload_form}
            id="CustomFieldModal/DisplayFieldOnUploadCheckbox"
            onClick={this.handleFlagForUpload}
          />
          <label
            className={classes.checkboxLabel}
            htmlFor="CustomFieldModal/DisplayFieldOnUploadCheckbox"
          >
            <FormattedMessage
              id="CustomFieldModal.displayFieldOnUpload"
              defaultMessage="Display this field on upload"
              description="Display field on upload checkbox label"
            />
          </label>
          <div
            className={classes.tooltip}
            data-tip
            data-for="uploadFieldTooltip"
          >
            <Icon
              size="s"
              color="status.active"
              icon="help"
              placement="right"
              id="uploadFieldTooltip"
              tooltip={intl.formatMessage({
                id: 'CustomFieldModal.uploadFieldTooltip',
                defaultMessage:
                  "Checking this box will cause this field to appear every time a user uploads documents. It's important to keep this limited to your most important 2-3 fields.",
                description: 'Upload field tooltip text',
              })}
            />
          </div>
        </span>
      </div>
    );
  }

  renderDocTypesExceptions() {
    const { classes } = this.props;
    const { selectedItem, docTypes } = this.state;

    if (selectedItem && !!selectedItem.is_smart) {
      return null;
    }
    return (
      <div className={classes.optionWrapper}>
        <span className={classes.optionLabelText}>
          <FormattedMessage
            id="CustomFieldModal.except"
            defaultMessage="except"
            description="Exception option label"
          />
        </span>
        <div className={classes.optionSelectionContainer}>
          {this.renderMultiSelect(docTypes, 'doc_types')}
        </div>
      </div>
    );
  }

  renderDocTypesSpecific() {
    const { classes } = this.props;
    const { selectedItem, docTypes } = this.state;

    if (selectedItem && !!selectedItem.is_smart) {
      return null;
    }
    return (
      <div className={classes.optionWrapper}>
        <span className={classes.optionLabelText}>
          <FormattedMessage
            id="CustomFieldModal.specific"
            defaultMessage="specific"
            description="Specific option label"
          />
        </span>
        <div className={classes.optionSelectionContainer}>
          {this.renderMultiSelect(docTypes, 'doc_types')}
        </div>
      </div>
    );
  }

  render() {
    const {
      classes,
      title,
      selectedSection,
      confirmButtonText,
      hideModal,
      selectedItem,
      intl,
      hideFooter,
    } = this.props;
    const {
      editData: {
        name,
        defaultValue,
        field_type,
        allow_new_options,
        options,
        doc_restriction,
        doc_types,
      },
    } = this.state;

    const multiValuesConditionNotOk =
      !!field_type &&
      field_type.value === 'Dropdown' &&
      !allow_new_options &&
      !options.length;

    const multiValuesHasNoMatchingDefault =
      !!field_type &&
      field_type.value === 'Dropdown' &&
      defaultValue &&
      defaultValue.length &&
      options &&
      options.filter((option) => option.value === defaultValue).length === 0;

    const disabledSaveBtn =
      !name ||
      multiValuesConditionNotOk ||
      multiValuesHasNoMatchingDefault ||
      (doc_restriction === 'INCLUDE' && !doc_types.length);
    const isWssField = selectedItem?.static_key_name;

    const shouldHideFooter = hideFooter === true;

    const content = (
      <div>
        <label
          className={classes.labelText}
          htmlFor="CustomFieldModal/FieldName"
        >
          <FormattedMessage
            id="CustomFieldModal.fieldNameLabel"
            defaultMessage="FIELD NAME"
            description="Field name input label"
          />
        </label>
        <EcInput
          id="CustomFieldModal/FieldName"
          isDisabled={isWssField}
          input={{
            'data-testid': 'data-field-name-input',
            value: name,
            onChange: (event) => {
              this.handleFormChange('name', event.target.value);
            },
          }}
        />

        {this.renderFieldType()}

        <div className={classes.spacer} />

        {this.renderAvailableOption()}

        {this.renderAdvancedOptions()}

        {this.renderDocumentTypes()}
      </div>
    );

    if (shouldHideFooter) {
      return content;
    }

    return (
      <EcModalCard
        title={`${title} → ${selectedSection}`}
        content={content}
        footer={
          <>
            <Button
              variant="tertiary"
              text={intl.formatMessage({
                id: 'CustomFieldModal.cancelButton',
                defaultMessage: 'Cancel',
                description: 'Cancel button text',
              })}
              onClick={hideModal}
            />
            <Button
              variant="primary"
              text={confirmButtonText}
              disabled={disabledSaveBtn}
              onClick={this.handleActionClick}
            />
          </>
        }
        hideModal={hideModal}
      />
    );
  }
}

const mapStateToProps = ({ currentUser }) => ({ currentUser });

CustomFieldModal = withFlags(
  connect(mapStateToProps)(injectIntl(CustomFieldModal)),
);

export default injectSheet(styles)(withFormatDate(CustomFieldModal));
